import React, { useEffect, useMemo, useState } from "react";
import { Toaster, toast } from "sonner";
import DashboardShell from "../components/layout/DashboardShell";
import api, { formatApiError } from "../lib/api";
import { Coins, TrendingUp, IndianRupee } from "lucide-react";

/**
 * "My incentives" — an agent-facing (and any admin-facing) rollup of the
 * user's own incentive payouts across months. Read-only.
 */
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const STATUS_COLOR = {
  accrued: "bg-yellow-100 text-yellow-900",
  verified: "bg-blue-100 text-blue-900",
  approved: "bg-emerald-100 text-emerald-900",
  paid: "bg-green-800 text-white",
  hold: "bg-red-100 text-red-900",
};

export default function MyIncentives() {
  const [rows, setRows] = useState([]);
  const [monthly, setMonthly] = useState({});
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/incentive-payouts/me");
      setRows(data.items || []);
      setMonthly(data.monthly || {});
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const totalEarned = useMemo(() => {
    return Object.values(monthly).reduce((acc, m) => acc + (m.paid || 0), 0);
  }, [monthly]);
  const totalInFlight = useMemo(() => {
    return Object.values(monthly).reduce((acc, m) => acc + (m.accrued || 0) + (m.verified || 0) + (m.approved || 0), 0);
  }, [monthly]);
  const totalOnHold = useMemo(() => Object.values(monthly).reduce((acc, m) => acc + (m.hold || 0), 0), [monthly]);

  const months = useMemo(() => Object.keys(monthly).sort().reverse(), [monthly]);

  return (
    <DashboardShell title="My Incentives" subtitle="Debt CRM · Your live incentive payout ledger">
      <Toaster richColors position="top-right" />
      <div className="space-y-5" data-testid="my-incentives">
        {/* Hero KPI tiles */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <KpiTile label="Total paid to date" value={inr(totalEarned)} icon={IndianRupee} accent />
          <KpiTile label="In-flight (pending review)" value={inr(totalInFlight)} icon={TrendingUp} />
          <KpiTile label="On hold" value={inr(totalOnHold)} icon={Coins} muted={totalOnHold === 0} />
        </div>

        {/* Monthly breakdown */}
        <div className="border border-ink/10 rounded-lg bg-white overflow-auto">
          <div className="px-4 py-3 border-b border-ink/10 text-xs font-heading text-navy uppercase tracking-wider">Monthly breakdown</div>
          {loading ? (
            <div className="p-4 text-xs italic text-ink-muted">Loading…</div>
          ) : months.length === 0 ? (
            <div className="p-4 text-xs italic text-ink-muted">No payouts yet. Once your disbursements land, they&apos;ll show up here.</div>
          ) : (
            <table className="w-full text-[12px]">
              <thead className="bg-navy/5 text-navy">
                <tr>
                  <th className="text-left px-4 py-2">Month</th>
                  <th className="text-right px-4 py-2">Accrued</th>
                  <th className="text-right px-4 py-2">Verified</th>
                  <th className="text-right px-4 py-2">Approved</th>
                  <th className="text-right px-4 py-2">Paid</th>
                  <th className="text-right px-4 py-2">On hold</th>
                </tr>
              </thead>
              <tbody>
                {months.map((m) => {
                  const b = monthly[m];
                  return (
                    <tr key={m} className="border-b border-ink/5" data-testid={`myinc-month-${m}`}>
                      <td className="px-4 py-2 font-mono">{m}</td>
                      <td className="px-4 py-2 text-right font-mono">{inr(b.accrued)}</td>
                      <td className="px-4 py-2 text-right font-mono">{inr(b.verified)}</td>
                      <td className="px-4 py-2 text-right font-mono">{inr(b.approved)}</td>
                      <td className="px-4 py-2 text-right font-mono text-emerald-700 font-heading">{inr(b.paid)}</td>
                      <td className="px-4 py-2 text-right font-mono">{inr(b.hold)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Per-payout ledger */}
        <div className="border border-ink/10 rounded-lg bg-white overflow-auto">
          <div className="px-4 py-3 border-b border-ink/10 text-xs font-heading text-navy uppercase tracking-wider">All payouts</div>
          <table className="w-full text-[12px]">
            <thead className="bg-navy/5 text-navy">
              <tr>
                <th className="text-left px-4 py-2">Month</th>
                <th className="text-left px-4 py-2">Rule</th>
                <th className="text-left px-4 py-2">Event</th>
                <th className="text-right px-4 py-2">Amount</th>
                <th className="text-right px-4 py-2">Multiplier</th>
                <th className="text-left px-4 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {!loading && rows.length === 0 && <tr><td colSpan={6} className="p-4 text-xs italic text-ink-muted">No payouts yet.</td></tr>}
              {rows.map((r) => (
                <tr key={r.payout_id} className="border-b border-ink/5" data-testid={`myinc-row-${r.payout_id}`}>
                  <td className="px-4 py-2 font-mono">{r.period}</td>
                  <td className="px-4 py-2">{r.rule_name}</td>
                  <td className="px-4 py-2 font-mono text-[10px] text-ink-muted">{r.trigger_event}</td>
                  <td className="px-4 py-2 text-right font-mono text-navy font-heading">{inr(r.amount_inr)}</td>
                  <td className="px-4 py-2 text-right font-mono">{r.team_multiplier_applied}×</td>
                  <td className="px-4 py-2">
                    <span className={`text-[10px] font-heading px-1.5 py-0.5 rounded-full uppercase ${STATUS_COLOR[r.status] || "bg-ink/5"}`}>{r.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="text-[10px] text-ink-muted italic">
          Payouts accrue live at 1× on every sanction / disbursement. Team-multiplier bonus is applied
          at month-end once your team&apos;s target achievement is finalized. Finance verifies, Debt BU
          Head approves, and finance releases the payout.
        </div>
      </div>
    </DashboardShell>
  );
}

function KpiTile({ label, value, icon: Icon, accent, muted }) {
  return (
    <div className={`rounded-lg border ${accent ? "border-navy bg-navy text-yellow-300" : "border-ink/10 bg-white"} p-4 ${muted ? "opacity-60" : ""}`}>
      <div className={`text-[10px] font-heading uppercase tracking-wider mb-1 ${accent ? "text-yellow-300/80" : "text-ink-muted"} flex items-center gap-1`}>
        <Icon size={11} /> {label}
      </div>
      <div className={`text-lg font-heading ${accent ? "text-yellow-300" : "text-navy"}`}>{value}</div>
    </div>
  );
}
